/**
 * `ReviewPanel` — pagina di valutazione per `reviewer`, `manager` e `admin`.
 *
 * La pagina ha due viste:
 *
 * 1. **Lista del team** (default): elenco degli utenti valutabili con ricerca e
 *    filtro per reparto (stesso pattern delle altre sezioni). Per ogni utente
 *    mostra la data della mia ultima review e se è stato revisionato da altri.
 *    Il reviewer (che può valutare un solo reparto) ha il filtro reparto
 *    disabilitato e pre-selezionato sul proprio reparto; manager e admin lo
 *    usano liberamente. Un pulsante per riga avvia la review dell'utente.
 *
 * 2. **Review del singolo utente**: la stessa vista di valutazione (un selettore
 *    di Livello per skill, invio in un unico batch) **senza** il selettore
 *    laterale delle persone. Un pulsante riporta alla lista.
 *
 * Lo **scoping è autorevole lato API**: `GET /api/review/users` restituisce già
 * gli utenti visibili in base al ruolo e al reparto del richiedente (reviewer →
 * solo il proprio reparto, manager/admin → tutti — 8.4/8.5) arricchiti con lo
 * stato di review (`ReviewableUserDto`). Selezionato un utente si caricano i
 * suoi Assessment (`GET /api/users/:id/assessments`) e l'ultima submission del
 * richiedente (`GET /api/users/:id/assessment-submissions/mine`) per precaricare
 * i picker.
 *
 * Il valutatore vota un **Livello** (`e-1..e-5`) per ogni skill — sempre
 * editabile, un voto per skill — e **invia tutto insieme**: l'API crea una
 * submission datata unica (`POST /api/users/:id/assessment-submissions`) con
 * `scores: { skillId, livello }` e decide, in base al ruolo del richiedente, se
 * registrarla come `reviewer` o `manager` (l'admin agisce da manager). Il
 * Livello_Self del target e la sua fascia di Esperienza (dato informativo)
 * sono mostrati in **sola lettura** (8.1, 8.7).
 *
 * _Requirements: 8.3, 2.1, 2.2, 8.2, 8.4, 8.5, 8.6._
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentSubmissionDto,
  AssessmentView,
  CompetenceDto,
  CreateSubmissionRequest,
  DepartmentDto,
  ExperienceRange,
  Livello,
  ReviewableUserDto,
  SkillDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { LivelloBadge } from '../components/LivelloBadge';
import { LivelloGuide } from '../components/LivelloGuide';
import { LivelloSelect } from '../components/LivelloSelect';
import { EXPERIENCE_RANGE_LABELS } from '../shared/dto';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Icon,
  InfoHint,
  Loading,
  PageHeader,
  TextInput,
  type Column,
} from '../components/ui';

/** Chiavi di query per utenti, reparti, assessment e submission del target. */
// Chiave dedicata alla lista scopata della Review: distinta da `['users']`
// (lista admin di UserAdmin) per evitare collisioni di cache tra endpoint.
const usersKey = ['review', 'users'] as const;
const departmentsKey = ['departments'] as const;
const skillsKey = ['skills'] as const;
const competencesKey = ['competences'] as const;

/**
 * Riga della tabella di review: una per **ogni** skill del catalogo (anche
 * quelle mai valutate), arricchita con i dati dell'assessment del target quando
 * presenti.
 */
interface ReviewSkillRow {
  skillId: string;
  skillName: string;
  description: string | undefined;
  /** Competenze a cui la skill è collegata (per il filtro per competenza). */
  competenceIds: string[];
  /** Livello dichiarato in autovalutazione dal target, o `null` se assente. */
  livelloSelf: Livello | null;
  /**
   * Fascia di anni di esperienza dichiarata dal target, dato puramente
   * informativo, o `null` se non dichiarata.
   */
  yearsOfExperience: ExperienceRange | null;
}
const userAssessmentsKey = (userId: string) =>
  ['users', userId, 'assessments'] as const;
const mySubmissionKey = (userId: string) =>
  ['users', userId, 'assessment-submissions', 'mine'] as const;

/** Formatta una data ISO come `gg/mm/aaaa hh:mm`, o `-` se assente. */
function formatSubmittedAt(iso: string | null | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ReviewPanel() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { role, me } = useAuth();

  // Utente attualmente in review (vista di valutazione); `null` = vista lista.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Ricerca e filtro reparto della lista (pattern condiviso con le altre pagine).
  const [query, setQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<'all' | string>('all');

  // Ricerca e filtro competenza della tabella skill (vista di review singola).
  // Valori del filtro:
  // - `'all'`: tutte le skill del catalogo;
  // - `'self-rated'`: solo le skill con una valutazione self del target;
  // - `<id>`: solo le skill della competenza scelta.
  const [skillSearch, setSkillSearch] = useState('');
  const [competenceFilter, setCompetenceFilter] = useState<
    'all' | 'self-rated' | string
  >('all');

  // Bozza dei Livelli votati in editing: skillId → Livello (`null` = skill non
  // votata in questo invio). È il voto primario del reviewer/manager.
  const [draft, setDraft] = useState<Map<string, Livello | null>>(new Map());
  // Stato precaricato (ultima submission del richiedente): serve a rilevare se
  // la bozza è stata modificata, così l'invio si abilita solo dopo un
  // cambiamento reale.
  const [baseline, setBaseline] = useState<Map<string, Livello | null>>(new Map());

  // Il "Team Leader" (ruolo `reviewer` a DB) valuta solo i membri del proprio
  // team: l'API restituisce già l'elenco scopato al suo team, quindi il filtro
  // per business unit non è pertinente e viene bloccato sul proprio reparto.
  // Manager e admin filtrano liberamente per business unit.
  const isReviewer = role === 'reviewer';
  const lockedDepartmentId = isReviewer ? me?.departmentId ?? null : null;

  // Ruolo con cui l'API registrerà la submission (solo informativo per la UI).
  const raterLabel = isReviewer ? 'Team Leader' : 'Manager';

  // Elenco utenti valutabili: lo scoping per ruolo/reparto è applicato dall'API.
  // Si usa l'endpoint di Review (`/api/review/users`), NON `/api/users`
  // (admin-only): così manager e reviewer ottengono l'elenco scopato senza 403.
  const usersQuery = useQuery({
    queryKey: usersKey,
    queryFn: () => api.get<ReviewableUserDto[]>('/api/review/users'),
  });

  // Reparti: opzioni del filtro (per manager/admin) e nomi per la colonna.
  const departmentsQuery = useQuery({
    queryKey: departmentsKey,
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Catalogo skill completo: è la base delle righe di review (si valutano tutte
  // le skill, anche quelle mai valutate dal target), e fornisce descrizione e
  // associazioni a competenza per hint e filtro.
  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Competenze: opzioni del filtro per competenza nella vista di review.
  const competencesQuery = useQuery({
    queryKey: competencesKey,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  // Assessment del target selezionato (self + medie), caricati on-demand.
  const assessmentsQuery = useQuery({
    queryKey: selectedUserId
      ? userAssessmentsKey(selectedUserId)
      : ['users', 'none', 'assessments'],
    queryFn: () => api.get<AssessmentView[]>(`/api/users/${selectedUserId}/assessments`),
    enabled: selectedUserId !== null,
  });

  // Ultima submission del richiedente verso il target, per precaricare i picker.
  const mySubmissionQuery = useQuery({
    queryKey: selectedUserId
      ? mySubmissionKey(selectedUserId)
      : ['users', 'none', 'assessment-submissions', 'mine'],
    queryFn: () =>
      api.get<AssessmentSubmissionDto | null>(
        `/api/users/${selectedUserId}/assessment-submissions/mine`,
      ),
    enabled: selectedUserId !== null,
  });

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const departments = useMemo(
    () => departmentsQuery.data ?? [],
    [departmentsQuery.data],
  );

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  const assessments = useMemo(
    () => assessmentsQuery.data ?? [],
    [assessmentsQuery.data],
  );

  // Righe di review: **tutte** le skill del catalogo, così è possibile valutare
  // anche skill mai recensite dal target. I dati dell'assessment del target
  // (Livello_Self, Esperienza) arricchiscono la riga in sola
  // lettura quando presenti; altrimenti restano `null`.
  const skillRows = useMemo<ReviewSkillRow[]>(() => {
    const assessmentBySkill = new Map(assessments.map((a) => [a.skillId, a]));
    return (skillsQuery.data ?? [])
      .map((s) => {
        const a = assessmentBySkill.get(s.id);
        return {
          skillId: s.id,
          skillName: s.name,
          description: s.description,
          competenceIds: s.competenceIds,
          livelloSelf: a?.livelloSelf ?? null,
          yearsOfExperience: a?.yearsOfExperience ?? null,
        };
      })
      .sort((a, b) =>
        a.skillName.localeCompare(b.skillName, 'it', { sensitivity: 'base' }),
      );
  }, [skillsQuery.data, assessments]);

  // Competenze cui appartiene almeno una skill, come opzioni del filtro.
  const competenceOptions = useMemo(() => {
    const used = new Set<string>();
    for (const row of skillRows) {
      for (const cid of row.competenceIds) used.add(cid);
    }
    return (competencesQuery.data ?? [])
      .filter((c) => used.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));
  }, [competencesQuery.data, skillRows]);

  // Competenze del filtro raggruppate per reparto per l'optgroup: reparti in
  // ordine alfabetico e competenze in ordine alfabetico dentro ciascuno;
  // "Senza reparto" (o reparto non più esistente) in coda.
  const competenceGroups = useMemo(() => {
    const departmentNameById = new Map(departments.map((d) => [d.id, d.name]));
    const ORPHAN = '__none__';
    const byDepartment = new Map<string, CompetenceDto[]>();
    for (const c of competenceOptions) {
      const key =
        c.departmentId !== null && departmentNameById.has(c.departmentId)
          ? c.departmentId
          : ORPHAN;
      const list = byDepartment.get(key) ?? [];
      list.push(c);
      byDepartment.set(key, list);
    }
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    const groups = [...byDepartment.entries()].map(([key, items]) => ({
      key,
      label: key === ORPHAN ? 'Senza reparto' : departmentNameById.get(key) ?? 'Senza reparto',
      isOrphan: key === ORPHAN,
      // `competenceOptions` è già ordinato per nome: l'ordine è preservato.
      competences: items,
    }));
    groups.sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
      return collator.compare(a.label, b.label);
    });
    return groups;
  }, [competenceOptions, departments]);

  // Righe visibili: filtro per competenza + ricerca per nome (case-insensitive).
  const normalizedSkillSearch = skillSearch.trim().toLowerCase();
  const visibleSkillRows = useMemo(() => {
    const byCompetence =
      competenceFilter === 'all'
        ? skillRows
        : competenceFilter === 'self-rated'
          ? skillRows.filter((r) => r.livelloSelf !== null)
          : skillRows.filter((r) => r.competenceIds.includes(competenceFilter));
    if (normalizedSkillSearch === '') return byCompetence;
    return byCompetence.filter((r) =>
      r.skillName.toLowerCase().includes(normalizedSkillSearch),
    );
  }, [skillRows, competenceFilter, normalizedSkillSearch]);

  // Nome reparto da id (per la colonna Business Unit).
  const departmentName = useMemo(() => {
    const byId = new Map(departments.map((d) => [d.id, d.name]));
    return (id: string | null) => (id ? byId.get(id) ?? id : '—');
  }, [departments]);

  // Allinea il filtro reparto al vincolo del reviewer: quando conosciamo il suo
  // reparto lo forziamo come unico valore selezionabile.
  useEffect(() => {
    if (isReviewer && lockedDepartmentId !== null) {
      setDepartmentFilter(lockedDepartmentId);
    }
  }, [isReviewer, lockedDepartmentId]);

  // Filtro per business unit + ricerca client-side per nome o email
  // (case-insensitive, sottostringa). Il reviewer resta sempre sul proprio
  // reparto (l'API già lo scopa, questo mantiene coerente la UI).
  const normalizedQuery = query.trim().toLowerCase();
  const effectiveDepartment =
    isReviewer && lockedDepartmentId !== null ? lockedDepartmentId : departmentFilter;
  const visibleUsers = useMemo(() => {
    const byDepartment =
      effectiveDepartment === 'all'
        ? users
        : users.filter((u) => (u.departmentId ?? '') === effectiveDepartment);
    if (normalizedQuery === '') return byDepartment;
    return byDepartment.filter(
      (u) =>
        u.displayName.toLowerCase().includes(normalizedQuery) ||
        u.email.toLowerCase().includes(normalizedQuery),
    );
  }, [users, normalizedQuery, effectiveDepartment]);

  // Precarica la bozza con l'ultima submission del richiedente quando i dati
  // (assessment + submission) del target sono pronti. Le skill non ancora
  // votate dal richiedente partono da `null` (non votato).
  useEffect(() => {
    if (
      selectedUserId === null ||
      !assessmentsQuery.isSuccess ||
      !mySubmissionQuery.isSuccess
    ) {
      return;
    }
    // L'ultima submission del richiedente trasporta ora un Livello per skill.
    const previous = new Map<string, Livello>();
    for (const s of mySubmissionQuery.data?.scores ?? []) {
      previous.set(s.skillId, s.livello);
    }
    const next = new Map<string, Livello | null>();
    for (const row of skillRows) {
      // Skill già votate dal richiedente partono dal Livello inviato; le altre
      // da `null` (non votate in questo invio).
      next.set(row.skillId, previous.get(row.skillId) ?? null);
    }
    setDraft(next);
    setBaseline(new Map(next));
    // Reset quando cambia utente o arrivano nuovi dati del server.
  }, [
    selectedUserId,
    assessmentsQuery.isSuccess,
    mySubmissionQuery.isSuccess,
    mySubmissionQuery.data,
    skillRows,
  ]);

  // Invio della valutazione come submission datata unica.
  const submit = useMutation({
    mutationFn: (vars: { userId: string; body: CreateSubmissionRequest }) =>
      api.post<AssessmentSubmissionDto>(
        `/api/users/${vars.userId}/assessment-submissions`,
        vars.body,
      ),
    onSuccess: (_data, vars) => {
      // Le medie e la bozza precaricata vanno rilette dopo l'invio.
      void queryClient.invalidateQueries({ queryKey: userAssessmentsKey(vars.userId) });
      void queryClient.invalidateQueries({ queryKey: mySubmissionKey(vars.userId) });
      // Aggiorna la lista (data ultima review) per il prossimo ritorno.
      void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });



  // Imposta il Livello votato per una skill nella bozza corrente.
  const setDraftLivello = (skillId: string, livello: Livello) => {
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(skillId, livello);
      return next;
    });
  };

  // Annulla le modifiche della riga in questo invio, riportando il Livello
  // votato al valore precaricato (baseline). Non tocca lo storico già inviato.
  const clearRow = (skillId: string) => {
    setDraft((prev) => {
      const next = new Map(prev);
      next.set(skillId, baseline.get(skillId) ?? null);
      return next;
    });
  };

  const startReview = (userId: string) => {
    setSelectedUserId(userId);
    submit.reset();
  };

  const backToList = () => {
    setSelectedUserId(null);
    submit.reset();
  };

  const handleSubmit = () => {
    if (selectedUser === null) return;
    // Invia solo le skill effettivamente votate (Livello non `null`).
    const scores = skillRows
      .map((r) => ({ skillId: r.skillId, livello: draft.get(r.skillId) ?? null }))
      .filter(
        (s): s is { skillId: string; livello: Livello } => s.livello !== null,
      );
    if (scores.length === 0) return;

    submit.mutate({
      userId: selectedUser.id,
      body: { scores },
    });
  };

  // Numero di skill con un Livello votato nella bozza corrente.
  const votedCount = skillRows.reduce(
    (n, r) => (draft.get(r.skillId) != null ? n + 1 : n),
    0,
  );

  // `true` se almeno un Livello votato della bozza differisce dallo stato
  // precaricato: l'invio si abilita solo dopo una modifica reale.
  const isDirty = useMemo(() => {
    for (const r of skillRows) {
      if ((draft.get(r.skillId) ?? null) !== (baseline.get(r.skillId) ?? null)) {
        return true;
      }
    }
    return false;
  }, [skillRows, draft, baseline]);

  const detailLoading =
    assessmentsQuery.isLoading ||
    mySubmissionQuery.isLoading ||
    skillsQuery.isLoading;
  const detailError =
    assessmentsQuery.isError || mySubmissionQuery.isError || skillsQuery.isError;

  // -------------------------------------------------------------------------
  // Colonne della lista del team.
  // -------------------------------------------------------------------------
  const columns = useMemo<Column<ReviewableUserDto>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Utente',
        sortable: true,
        sortAccessor: (u) => (u.displayName || u.email).toLowerCase(),
        // Nome + chip business unit affiancati (stesso pattern della tabella
        // Livelli/Team). La business unit è mostrata solo se assegnata.
        render: (u) => (
          <div className="coverage-name">
            <span className="coverage-name__label">{u.displayName || u.email}</span>
            {u.departmentId !== null && (
              <Badge tone="neutral">{departmentName(u.departmentId)}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'myLastReviewAt',
        header: 'Revisionato',
        sortable: true,
        // Le review assenti (`null`) vanno sempre in coda a prescindere dal verso.
        sortComparator: (a, b, direction) => {
          const va = a.myLastReviewAt;
          const vb = b.myLastReviewAt;
          if (va === null && vb === null) return 0;
          if (va === null) return 1;
          if (vb === null) return -1;
          const sign = direction === 'desc' ? -1 : 1;
          return (new Date(va).getTime() - new Date(vb).getTime()) * sign;
        },
        // Per la propria riga non c'è auto-review: si mostra "Tu".
        // Altrimenti chip con pallino verde + data se già valutato dal
        // richiedente, oppure chip con pallino rosso ("Mai valutato").
        render: (u) =>
          u.id === me?.id ? (
            <span className="review-list__chip">Tu</span>
          ) : u.myLastReviewAt ? (
            <span className="review-list__chip review-list__chip--done">
              <span className="review-list__dot review-list__dot--green" aria-hidden />
              {formatSubmittedAt(u.myLastReviewAt)}
            </span>
          ) : (
            <span className="review-list__chip review-list__chip--todo">
              <span className="review-list__dot review-list__dot--red" aria-hidden />
              Mai valutato
            </span>
          ),
      },
      {
        key: 'reviewedByOthers',
        header: 'Revisionato da altri',
        sortable: true,
        sortAccessor: (u) => (u.reviewedByOthers ? 1 : 0),
        render: (u) =>
          u.reviewedByOthers ? (
            <span className="review-list__badge review-list__badge--yes">Sì</span>
          ) : (
            <span className="review-list__badge review-list__badge--no">No</span>
          ),
      },
      {
        key: 'action',
        header: '',
        // Nessuna auto-review: per la propria riga il tasto non è mostrato.
        render: (u) =>
          u.id === me?.id ? null : (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => startReview(u.id)}
            >
              Avvia review
            </Button>
          ),
      },
    ],
    [departmentName, me?.id],
  );

  // -------------------------------------------------------------------------
  // Vista di review del singolo utente (senza selettore laterale).
  // -------------------------------------------------------------------------
  if (selectedUser !== null) {
    const dataReady =
      assessmentsQuery.isSuccess &&
      mySubmissionQuery.isSuccess &&
      skillsQuery.isSuccess;
    const canSubmit = dataReady && skillRows.length > 0;
    return (
      <section aria-labelledby="review-title">
        <PageHeader
          title={`Review di ${selectedUser.displayName || selectedUser.email}`}
          titleId="review-title"
          subtitle={`Assegna il Livello ${raterLabel} alle skill, poi invia la valutazione.`}
          actions={
            <Button type="button" variant="ghost" onClick={backToList}>
              ← Torna alla lista
            </Button>
          }
        />

        <div className="review-detail">
          {detailLoading && <Loading>Caricamento valutazione…</Loading>}
          {detailError && (
            <Alert tone="error">Impossibile caricare la valutazione.</Alert>
          )}
          {dataReady && skillRows.length === 0 && (
            <EmptyState role="status">Nessuna skill da valutare.</EmptyState>
          )}
          {canSubmit && (
            <>
              {/* Ricerca + filtro competenza a sinistra, invio a destra (stessa
                  riga). Il filtro/ricerca opera solo sulla visibilità: l'invio
                  considera comunque tutte le skill votate. */}
              <Field
                id="review-skill-search"
                label="Cerca skill"
                hint={`Ultima tua valutazione in data: ${formatSubmittedAt(
                  mySubmissionQuery.data?.submittedAt,
                )}`}
              >
                <div className="review-detail__toolbar">
                  <div className="competence-search">
                    <span className="competence-search__department user-admin__select">
                      <select
                        id="review-skill-competence"
                        aria-label="Filtra per competenza"
                        value={competenceFilter}
                        disabled={competencesQuery.isLoading}
                        onChange={(e) => setCompetenceFilter(e.target.value)}
                      >
                        <option value="all">Tutte le skill</option>
                        <option value="self-rated">
                          Solo skill auto valutate
                        </option>
                        {competenceGroups.map((group) => (
                          <optgroup key={group.key} label={group.label}>
                            {group.competences.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </span>
                    <TextInput
                      id="review-skill-search"
                      className="competence-search__input"
                      type="search"
                      value={skillSearch}
                      onChange={(e) => setSkillSearch(e.target.value)}
                      placeholder="Cerca una skill…"
                      autoComplete="off"
                      aria-label="Cerca una skill per nome"
                    />
                  </div>
                  <Button
                    type="submit"
                    form="review-form"
                    variant="primary"
                    disabled={submit.isPending || votedCount === 0 || !isDirty}
                  >
                    {submit.isPending ? 'Invio…' : 'Invia valutazione'}
                  </Button>
                </div>
              </Field>

              <form
                id="review-form"
                className="review-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {visibleSkillRows.length === 0 ? (
                  <EmptyState role="status">
                    Nessuna skill corrisponde alla competenza o alla ricerca.
                  </EmptyState>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Skill</th>
                        <th scope="col">
                          <span className="score-calc-guide__header">
                            Livello (Self)
                            <LivelloGuide className="score-calc-guide__trigger" />
                          </span>
                        </th>
                        <th scope="col">Esperienza</th>
                        <th scope="col">Il tuo voto ({raterLabel})</th>
                        <th scope="col">
                          <span className="sr-only">Azioni</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSkillRows.map((r) => (
                        <tr key={r.skillId}>
                          <th scope="row">
                            <span className="review-form__skill-label">
                              {r.skillName}
                              <InfoHint
                                text={r.description}
                                label={`Descrizione di ${r.skillName}`}
                              />
                            </span>
                          </th>
                          {/* Livello_Self dichiarato dal target, in sola
                              lettura. Il Livello_Self resta esclusivo dello
                              User: il valutatore non lo modifica. */}
                          <td>
                            {r.livelloSelf === null ? (
                              <span className="review-form__level-absent">
                                Non dichiarato
                              </span>
                            ) : (
                              <LivelloBadge livello={r.livelloSelf} label="Livello Self" />
                            )}
                          </td>
                          {/* Fascia di esperienza del target, dato puramente
                              informativo, in sola lettura (etichetta testuale). */}
                          <td>
                            {r.yearsOfExperience === null ? (
                              <span
                                className="skill-list__absent"
                                aria-label="Esperienza: non dichiarata"
                              >
                                –
                              </span>
                            ) : (
                              <span className="skill-list__experience">
                                {EXPERIENCE_RANGE_LABELS[r.yearsOfExperience]}
                              </span>
                            )}
                          </td>
                          {/* Voto primario del reviewer/manager: un Livello,
                              sempre editabile (un voto per skill per submission).
                              La scelta entra nella bozza e va nell'invio bulk;
                              le skill non votate (`null`) sono escluse. Stile
                              "pill" identico all'autovalutazione. */}
                          <td>
                            <span
                              className="user-admin__select skill-list__picker-level"
                              data-empty={
                                (draft.get(r.skillId) ?? null) === null || undefined
                              }
                            >
                              <LivelloSelect
                                value={draft.get(r.skillId) ?? null}
                                label={`Il tuo voto per ${r.skillName}`}
                                disabled={submit.isPending}
                                onChange={(livello) =>
                                  setDraftLivello(r.skillId, livello)
                                }
                              />
                            </span>
                          </td>
                          {/* Annulla le modifiche della riga in questo invio,
                              riportando il Livello votato al valore precaricato.
                              Il bottone occupa sempre lo spazio (nascosto quando
                              la riga non è modificata) per non far muovere il
                              layout alla sua comparsa. */}
                          <td className="admin-table__actions-col">
                            {(() => {
                              const changed =
                                (draft.get(r.skillId) ?? null) !==
                                (baseline.get(r.skillId) ?? null);
                              return (
                                <div className="admin-item__actions">
                                  <Button
                                    type="button"
                                    size="sm"
                                    iconOnly
                                    aria-label={`Annulla le modifiche di ${r.skillName}`}
                                    title="Annulla le modifiche di questa skill"
                                    disabled={submit.isPending || !changed}
                                    aria-hidden={!changed}
                                    tabIndex={changed ? undefined : -1}
                                    style={changed ? undefined : { visibility: 'hidden' }}
                                    onClick={() => clearRow(r.skillId)}
                                  >
                                    <Icon name="close" />
                                  </Button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <p className="review-form__hint">
                  {votedCount === 0
                    ? 'Assegna almeno un Livello per inviare.'
                    : !isDirty
                      ? 'Modifica almeno un Livello per inviare una nuova valutazione.'
                      : `${votedCount} skill valutate in questo invio.`}
                </p>

                {submit.isSuccess && (
                  <Alert tone="success">Valutazione inviata.</Alert>
                )}
                {submit.isError && (
                  <Alert tone="error">Impossibile inviare la valutazione. Riprova.</Alert>
                )}
              </form>
            </>
          )}
        </div>
      </section>
    );
  }

  // -------------------------------------------------------------------------
  // Vista lista del team (con ricerca e filtro reparto).
  // -------------------------------------------------------------------------
  return (
    <section className="review-panel" aria-labelledby="review-title">
      <PageHeader
        title="Review"
        titleId="review-title"
        subtitle="Scegli una persona del team e avvia la review delle sue skill."
      />

      {departmentsQuery.isError && (
        <Alert tone="warning">
          Impossibile caricare le business unit: il filtro per business unit è
          temporaneamente non disponibile.
        </Alert>
      )}

      {/* Ricerca a piena larghezza con filtro business unit integrato (select a
          1/4, input per il resto), stesso formato delle altre pagine. Per il
          reviewer il filtro è bloccato sul proprio reparto. */}
      <Field
        id="review-search"
        label="Cerca persona"
        hint={
          isReviewer
            ? 'La ricerca opera sul tuo reparto.'
            : 'Filtra per business unit, nome o email. La ricerca opera nella business unit selezionata.'
        }
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            <select
              id="review-search-department"
              aria-label="Filtra per business unit"
              value={effectiveDepartment}
              disabled={isReviewer || departmentsQuery.isLoading}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="all">Tutte le business unit</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </span>
          <TextInput
            id="review-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o email…"
            autoComplete="off"
            aria-label="Cerca per nome o email"
          />
        </div>
      </Field>

      {usersQuery.isLoading ? (
        <Loading>Caricamento utenti…</Loading>
      ) : (
        <DataTable
          rows={visibleUsers}
          columns={columns}
          rowKey={(u) => u.id}
          error={usersQuery.isError ? 'Impossibile caricare gli utenti.' : null}
          emptyMessage={
            normalizedQuery !== '' || effectiveDepartment !== 'all'
              ? 'Nessuna persona corrisponde alla business unit o alla ricerca.'
              : 'Nessuna persona valutabile.'
          }
          defaultSort={{ key: 'displayName', direction: 'asc' }}
          pageSize={10}
        />
      )}
    </section>
  );
}
